import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import api from "../lib/api";

const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

/** Extract up to `max` unique URLs from free text. */
export function extractUrls(text, max = 2) {
  if (!text) return [];
  const found = text.match(URL_RE) || [];
  const clean = found.map((u) => u.replace(/[),.;!?]+$/, ""));
  return [...new Set(clean)].slice(0, max);
}

function PreviewCard({ url }) {
  const [meta, setMeta] = useState(undefined); // undefined = loading, null = failed/empty

  useEffect(() => {
    let alive = true;
    api.get("/link-preview", { params: { url } })
      .then((r) => { if (alive) setMeta(r.data && (r.data.title || r.data.image) ? r.data : null); })
      .catch(() => { if (alive) setMeta(null); });
    return () => { alive = false; };
  }, [url]);

  if (!meta) return null; // loading or nothing worth showing

  return (
    <a href={url} target="_blank" rel="noreferrer nofollow" data-testid="link-preview-card"
      className="flex items-stretch border border-zinc-300 bg-zinc-50 hover:border-primary transition-colors overflow-hidden max-w-xl group">
      {meta.image && (
        <img src={meta.image} alt="" loading="lazy"
          className="w-28 sm:w-36 object-cover shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}
      <div className="p-3 min-w-0 flex flex-col justify-center">
        <div className="text-xs text-muted-foreground font-mono uppercase tracking-wide flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> {meta.site_name || meta.domain}
        </div>
        {meta.title && <div className="font-700 text-sm text-zinc-900 mt-0.5 line-clamp-2 group-hover:text-primary transition-colors">{meta.title}</div>}
        {meta.description && <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{meta.description}</div>}
      </div>
    </a>
  );
}

/** Renders rich preview cards for any URLs found in `text`. */
export default function LinkPreviews({ text }) {
  const urls = extractUrls(text);
  if (urls.length === 0) return null;
  return (
    <div className="mt-3 space-y-2" data-testid="link-previews">
      {urls.map((u) => <PreviewCard key={u} url={u} />)}
    </div>
  );
}
